<template>
  <div
    class="w-full waiting-list-form flex flex-col"
    :class="{
      'waiting-list-form--full': !mobilePreview && !desktopPreview,
      'pointer-events-none': mobilePreview,
    }"
    :style="desktopPreview ? 'height: 850px' : ''"
    >
    <hosted-page-banner
      :brand-name="brandNameLocal"
      :brand-colour="brandColourLocal"
      :icon-path="iconPathLocal"
      :logo-path="logoPathLocal"
      :prefer-logo-over-icon="preferLogoOverIconLocal"
      :mobile-preview="mobilePreview"
      :desktop-preview="desktopPreview"
    >
      <h3
        v-if="!turnNumberIsValid"
        class="text-center text-inherit w-full mt-5"
        :class="[
          mobilePreview ? '' : '',
          desktopPreview ? '' : '',
          !mobilePreview && !desktopPreview && !openDay ? 'text-sm md:text-2xl' : '',
          !mobilePreview && !desktopPreview && openDay  && !turnNumberIsValid ? 'lg:text-3xl' : ''
          ]"
        v-html="title"
      >
      </h3>
    </hosted-page-banner>

    <div class="w-full bg-white p-4 md:p-base flex justify-center flex-grow items-center md:pt-8">
      <div v-if="(openDay && !turnNumber) || mobilePreview || desktopPreview"
           :class="[
             mobilePreview ? 'p-0 w-full' : 'vx-card hosted-card p-0 sm:p-8',
             desktopPreview ? 'w-1/2' : '',
             !mobilePreview && !desktopPreview ? 'w-full sm:w-2/3 xl:w-2/5' : '',
           ]">
        <form
          class="contact-list-create-or-edit-form"
          @submit.prevent
        >
          <div class="flex flex-row gap-x-4">
            <div class="w-1/2">
              <vs-input
                v-model.trim="firstName"
                class="w-full required"
                :label="$t('$General.FirstName')"
                :name="$t('$General.FirstName')"
                autocomplete="given-name"
                v-validate="'required|max:50'"
                data-vv-validate-on="blur|input"
                :danger="errors.has($t('$General.FirstName'))"
                :danger-text="errors.first($t('$General.FirstName'))"
                val-icon-danger="clear"
                @focus="handleFormStart"
              />
            </div>

            <div class="w-1/2">
              <vs-input
                v-model.trim="lastName"
                class="w-full"
                :label="`${$t('$General.LastName')}`"
                :name="$t('$General.LastName')"
                autocomplete="family-name"
                v-validate="'max:50'"
                data-vv-validate-on="blur|input"
                :danger="errors.has($t('$General.LastName'))"
                :danger-text="errors.first($t('$General.LastName'))"
                val-icon-danger="clear"
                @focus="handleFormStart"
              />
            </div>
          </div>

          <div class="flex flex-col gap-y-6 mt-6">
            <div class="w-full">
              <label
                class="vs-input--label required">
                {{$tc('$General.Phone')}}
              </label>
              <vue-tel-input
                v-model="phoneNational"
                :disabledFetchingCountry="true"
                :dynamicPlaceholder="false"
                :enabledCountryCode="true"
                :validCharactersOnly="true"
                :preferredCountries="['AU', 'CO']"
                :defaultCountry="codeCountry ? codeCountry : 'AU'"
                autocomplete="tel"
                @validate="(payload) => (this.phoneValidate = payload)"
                @input="() => (this.phoneInput = true)"
                @blur="() => (this.phoneInput = true)"
                @focus="handleFormStart">
              </vue-tel-input>
              <span
                v-show="invalidPhoneNumber"
                class="text-danger">
                {{ $t('$ContactModule.InvalidPhoneNumberMsg') }}
              </span>
              <span
                v-show="requiredPhoneNumber"
                class="text-danger">
                {{ $t('$ContactModule.PhoneNumberRequiredMsg') }}
              </span>
            </div>
            <div class="w-full">
              <vs-input
                v-model.trim="email"
                type="email"
                class="w-full"
                :label="$tc('$General.Email')"
                :name="$tc('$General.Email')"
                autocomplete="email"
                v-validate="{ rules: { email: true } }"
                data-vv-validate-on="blur|input"
                :danger="errors.has($tc('$General.Email'))"
                :danger-text="errors.first($tc('$General.Email'))"
                val-icon-danger="clear"
                @focus="handleFormStart"
              />
            </div>
          </div>

          <vs-divider />

          <div
            class="vx-row">
            <div class="vx-col w-full">
              <vs-checkbox
                v-model="optInAgreement"
                style="display: -webkit-inline-box !important; -webkit-box-align: start;"
              >
                {{ $t('OptInMsg') }}
              </vs-checkbox>
            </div>

            <div class="vx-col w-full mt-5 text-justify md:text-left">
              <small class="mt-3">
                By securing spot, you agree to receive queue communications, and accept our
                <a
                  v-if="policyType === $enums.Tenants.POLICY_TYPE.URL"
                  :href="linkPolicy"
                  target="_blank"
                  class="link-contextual privacy-policy-link text-gray-dark"
                >
                  privacy policy
                </a>
                <router-link
                  v-else
                  :to="`/privacy-policy/${$route.params.tenantUrlLinksSuffix}`"
                  target="_blank"
                  class="link-contextual font-bold privacy-policy-link text-gray-dark"
                  >
                  privacy policy
                </router-link>.
              </small>
            </div>
          </div>

          <vs-divider
            v-if="!mobilePreview"
          />

          <vs-button
            :style="`background: ${accentColourLocal} !important;
            color: ${accentContrastTextColor} !important`"
            class="send-button shadow-md"
            :class="mobilePreview ? 'w-full mt-base' : 'w-full md:w-auto'"
            @click="secureSpot">
            {{ $t('ConfirmButtonText') }}
          </vs-button>
        </form>
      </div>

      <div
        v-if="!openDay && !turnNumberIsValid"
        class="w-full md:w-1/2 lg:w-1/3">
        <waiting-list-form-opening-hours
          v-if="thereAreOpeningHours"
          :opening-hours="openingHours"/>
        <waiting-list-form-closed-svg
          v-else
          class="w-full h-auto"
          style="max-height: 100%"
          :color="accentColourLocal"/>
      </div>

      <div
        v-if="turnNumberIsValid"
        class="w-full flex justify-center items-center px-4">
        <waiting-list-form-success
          :color="accentColourLocal"
          :brand-color="brandColourLocal"
          :queue-position="turnNumberWithPrefix"
          :current-turn-number="currentTurnNumberWithPrefix"
          :estimated-wait="estimatedWait"
          :customer-name="customerFullName"
          :customer-phone="customerPhone"
          :customer-email="customerEmail"
          :turn-info="turnInfo"
          :tenant-url-links-suffix="tenantUrlLinksSuffix"
          :prefix-queue-position="prefixQueuePosition"
          @workday-closed="workdayClosed"
        />
      </div>
    </div>

    <powered-by-footer page-type="waiting-list"/>
  </div>
</template>

<script>
import { VueTelInput } from 'vue-tel-input';
import WaitingListFormClosedSvg from '@/views/pages/HostedWaitingListFormClosedSvg.vue';
import WaitingListFormSuccess from '@/views/pages/HostedWaitingListFormSuccess.vue';
import WaitingListFormOpeningHours from '@/views/pages/HostedWaitingListFormOpeningHours.vue';
import PoweredByFooter from '@/views/pages/_components/PoweredByFooter.vue';
import HostedPageBanner from '@/components/HostedPageBanner.vue';
import { getContrastColor } from '@/utils/colorUtils';
import waitingListService from '@/api/modules/waiting-list.service';
import themeConfig from '../../../themeConfig';

/**
 * Page of auth, wrap the login and register components
 *
 * @module views/pages/ContactOptOut
 * @author Dilan Useche <dilan8810@gmail.com>
 *
 */
export default {
  name: 'HostedWaitingListForm',
  components: {
    VueTelInput,
    WaitingListFormClosedSvg,
    WaitingListFormSuccess,
    WaitingListFormOpeningHours,
    PoweredByFooter,
    HostedPageBanner,
  },
  i18n: {
    messages: {
      en: {
        OpenTitle: 'Reserve your spot',
        CloseTitle: 'We\'re closed for now, but we look forward to seeing you when we reopen.',
        SentTitle1: 'We\'ve received your details, and your spot number is [Turn Number].',
        SentTitle2: 'You will receive text message notifications at [Phone Number].',
        ConfirmButtonText: 'Secure spot',
        OptInMsg: 'Join our list for special offers.',
        FooterMsg: 'By securing spot, you agree to receive queue communications, and accept our',
        PrivacyPolicy: 'Privacy Policy',
      },
    },
  },
  props: {
    mobilePreview: {
      type: Boolean,
      default: false,
    },
    desktopPreview: {
      type: Boolean,
      default: false,
    },
    brandName: {
      type: String,
      default: 'Company Name',
    },
    brandColour: {
      type: String,
      default: themeConfig.colors.primary,
    },
    accentColour: {
      type: String,
      default: themeConfig.colors.primary,
    },
    iconPath: {
      type: String,
      default: '',
    },
    logoPath: {
      type: String,
      default: '',
    },
    preferLogoOverIcon: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      phoneNational: '',
      codeCountry: '',

      phoneValidate: { isValid: false },
      phoneNumberInfo: {},
      phoneInput: '',
      optInAgreement: false,

      brandNameLocal: this.brandName,
      iconPathLocal: this.iconPath,
      logoPathLocal: this.logoPath,
      preferLogoOverIconLocal: this.preferLogoOverIcon,
      brandColourLocal: this.brandColour,
      accentColourLocal: this.accentColour,

      policyType: '',
      inlinePolicy: '',
      linkPolicy: '',

      openingHours: [],

      openDay: true,
      prefixQueuePosition: '',
      tenantUrlLinksSuffix: null,
      turnInfo: null,
      turnNumber: null,
      currentTurnNumber: null,
      estimatedWait: '5 mins',

      formStarted: false,

      // Prevent duplicate hosted page view tracking (e.g. if branding fetch retries)
      lastTrackedSafePath: '',
    };
  },
  computed: {
    title() {
      return !this.openDay ? this.$t('CloseTitle') : this.$t('OpenTitle');
    },
    showSentTitle() {
      return this.openDay && this.turnNumberIsValid;
    },
    accentContrastTextColor() {
      return this.accentColourLocal ? this.getContrastColor(this.accentColourLocal) : '';
    },
    verifyProperties() {
      return this.firstName !== '' && (this.phoneNational !== '' && this.phoneValidate.isValid);
    },
    invalidPhoneNumber() {
      return this.phoneInput && !this.phoneValidate.isValid && this.phoneNational !== '';
    },
    requiredPhoneNumber() {
      return this.phoneInput && !this.phoneValidate.isValid && this.phoneNational === '';
    },
    thereAreOpeningHours() {
      return this.openingHours.length > 0;
    },
    turnNumberIsValid() {
      return this.turnNumber === 0 || !!this.turnNumber;
    },
    turnNumberWithPrefix() {
      const separator = this.prefixQueuePosition ? '-' : '';

      return `${this.prefixQueuePosition || ''}${separator}${this.turnNumber || ''}`;
    },
    currentTurnNumberWithPrefix() {
      const separator = this.prefixQueuePosition ? '-' : '';

      return `${this.prefixQueuePosition || ''}${separator}${this.currentTurnNumber || ''}`;
    },
    customerFullName() {
      if (this.firstName && this.lastName) {
        const first = this.firstName.trim();
        const last = this.lastName.trim();
        if (first && last) {
          return `${first} ${last}`;
        }
        return first || last || '';
      }

      if (this.turnInfo && this.turnInfo.contact && this.turnInfo.contact.name) {
        return this.turnInfo.contact.name.trim();
      }

      return '';
    },
    customerEmail() {
      if (this.email) {
        return this.email;
      }

      if (this.turnInfo && this.turnInfo.contact && this.turnInfo.contact.email) {
        return this.turnInfo.contact.email.trim();
      }

      return '';
    },
    customerPhone() {
      if (this.phoneValidate.isValid) {
        return this.getPhoneNational();
      }

      if (this.turnInfo && this.turnInfo.contact && this.turnInfo.contact.phoneNational) {
        return this.turnInfo.contact.phoneNational;
      }

      return '';
    },
  },
  watch: {
    brandName(val) {
      this.brandNameLocal = val;
    },
    brandColour(val) {
      this.brandColourLocal = val;
    },
    accentColour(val) {
      this.accentColourLocal = val;
    },
    iconPath(val) {
      this.iconPathLocal = val;
    },
    logoPath(val) {
      this.logoPathLocal = val;
    },
    preferLogoOverIcon(val) {
      this.preferLogoOverIconLocal = val;
    },
  },
  async created() {
    // Add SEO meta tags to prevent search engine indexing
    this.addNoIndexMetaTags();

    const { tenantUrlLinksSuffix, waitingListId } = this.$route.params;

    if (tenantUrlLinksSuffix) {
      this.tenantUrlLinksSuffix = tenantUrlLinksSuffix;
      await this.fetchWaitingListBrandingDetails(tenantUrlLinksSuffix);
    } else if (!this.mobilePreview && !this.desktopPreview) {
      this.$router.push({ name: 'not-authorized' });
    }

    if (waitingListId) {
      await this.fetchWaitingListTurnDetails(waitingListId);
    }
  },
  methods: {
    async workdayClosed() {
      this.turnInfo = null;
      this.turnNumber = null;
      this.currentTurnNumber = null;

      this.removeWaitingListIdFromPath();

      const { tenantUrlLinksSuffix } = this.$route.params;
      this.tenantUrlLinksSuffix = tenantUrlLinksSuffix;

      await this.fetchWaitingListBrandingDetails(tenantUrlLinksSuffix);
    },
    addWaitingListIdToPath(waitingListId) {
      if (!waitingListId) return;

      // Tomamos el path actual sin slash final
      const { path, query, hash } = this.$route;
      const cleanPath = path.replace(/\/+$/, '');
      const segments = cleanPath.split('/');

      // Si ya está al final, no hacemos nada
      if (segments[segments.length - 1] === String(waitingListId)) {
        return;
      }

      const newPath = `${cleanPath}/${waitingListId}`;

      // Reemplazamos la URL manteniendo query y hash
      this.$router.replace({ path: newPath, query, hash }).catch(() => {});
    },
    removeWaitingListIdFromPath() {
      const {
        path, query, hash, params,
      } = this.$route;

      // Solo actuamos si existe waitingListId en los params
      if (!params || !params.waitingListId) return;

      const cleanPath = path.replace(/\/+$/, '');
      const segments = cleanPath.split('/');

      // Validamos que el último segmento sea exactamente el waitingListId actual
      if (segments[segments.length - 1] !== String(params.waitingListId)) return;

      // Quitamos el último segmento (waitingListId)
      segments.pop();

      const newPath = segments.join('/') || '/';

      // Reemplazamos manteniendo query y hash
      this.$router.replace({ path: newPath, query, hash }).catch(() => {});
    },
    addNoIndexMetaTags() {
      // Remove existing meta tags if they exist
      const existingRobotsTag = document.querySelector('meta[name="robots"]');
      const existingGooglebotTag = document.querySelector('meta[name="googlebot"]');

      if (existingRobotsTag) {
        existingRobotsTag.remove();
      }
      if (existingGooglebotTag) {
        existingGooglebotTag.remove();
      }

      // Add noindex meta tags
      const robotsTag = document.createElement('meta');
      robotsTag.name = 'robots';
      robotsTag.content = 'noindex, nofollow';
      document.head.appendChild(robotsTag);

      const googlebotTag = document.createElement('meta');
      googlebotTag.name = 'googlebot';
      googlebotTag.content = 'noindex, nofollow';
      document.head.appendChild(googlebotTag);
    },
    async fetchWaitingListTurnDetails(waitingListId) {
      this.$vs.loading({
        type: 'radius',
        color: this.accentColourLocal,
      });

      try {
        const response = await waitingListService.getOne({
          id: waitingListId,
        });
        
        // Check if entry was deleted (removed from queue)
        if (response.data && response.data.deleted) {
          // Entry was removed - redirect to form to allow rejoining
          this.removeWaitingListIdFromPath();
          this.$vs.loading.close();
          return;
        }
        
        // Check if a new workday has started (old queue no longer exists)
        if (response.data && response.data.newWorkdayStarted) {
          // Old queue is gone - clear turn info and show form/opening hours
          this.removeWaitingListIdFromPath();
          this.$vs.loading.close();
          return;
        }
        
        this.turnInfo = response.data;
        this.turnNumber = this.turnInfo.waitingList.turnNumber;
        this.currentTurnNumber = this.turnInfo.currentTurnNumber;
      } catch (err) {
        // Invalid waitingListId - redirect to form page (remove ID from path)
        this.removeWaitingListIdFromPath();
      } finally {
        this.$vs.loading.close();
      }
    },
    async fetchWaitingListBrandingDetails(tenantUrlLinksSuffix) {
      this.$vs.loading({
        type: 'radius',
        color: this.accentColourLocal,
      });

      try {
        const resp = await waitingListService.getWaitingListBrandingDetails(tenantUrlLinksSuffix);
        const {
          name,
          icon,
          logo,
          preferLogoOverIcon,
          brandColour,
          accentColor,
          policyType,
          inlinePolicy,
          linkPolicy,
          openDay,
          prefixQueuePosition,
          openingHours,
        } = resp.data;

        this.brandNameLocal = name || this.brandName;
        this.iconPathLocal = icon;
        this.logoPathLocal = logo;
        this.preferLogoOverIconLocal = preferLogoOverIcon;
        this.brandColourLocal = brandColour;
        this.accentColourLocal = accentColor;

        this.policyType = policyType;
        this.inlinePolicy = inlinePolicy;
        this.linkPolicy = linkPolicy;

        this.openingHours = openingHours || [];

        this.openDay = openDay;
        this.prefixQueuePosition = prefixQueuePosition;

        // Update page title
        this.updatePageTitle();

        // Track hosted page view after async branding + title update
        this.trackHostedPageView();
      } catch (err) {
        // Invalid tenant - redirect to 404 page
        this.$router.push({ name: 'page-error-404' });
      } finally {
        this.$vs.loading.close();
      }
    },
    updatePageTitle() {
      const prefix = String(this.$t('$General.WaitingList'));
      // Set page title to "Waitlist | Business Name" (translated prefix)
      document.title = `${prefix} | ${this.brandNameLocal}`;
    },
    trackHostedPageView() {
      const safePath = this.turnNumberIsValid
        ? '/waitlist/business-slug/customer-slug'
        : '/waitlist/business-slug';
      if (safePath === this.lastTrackedSafePath) return;

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'custom_page_view',
        page_path: safePath,
        page_title: 'Waitlist | Business Name',
      });

      this.lastTrackedSafePath = safePath;
    },
    getContrastColor,
    getPhoneInternationalE164() {
      if (this.phoneValidate && this.phoneValidate.number && this.phoneValidate.number.e164) {
        return this.phoneValidate.number.e164;
      }

      if (this.phoneNumberInfo && this.phoneNumberInfo.phoneInternationalSignificant) {
        return this.phoneNumberInfo.phoneInternationalSignificant;
      }

      return '';
    },

    getPhoneNational() {
      if (this.phoneValidate && this.phoneValidate.number && this.phoneValidate.number.national) {
        return this.phoneValidate.number.national;
      }

      return '';
    },
    async secureSpot() {
      this.phoneInput = true;
      const { tenantUrlLinksSuffix } = this.$route.params;
      const formIsValid = await this.$validator.validate();

      if (this.verifyProperties && formIsValid) {
        this.$vs.loading({
          type: 'radius',
          color: this.accentColourLocal,
        });

        try {
          const response = await waitingListService.bookTurnInWaitingList({
            firstName: this.$options.filters.capitalize(this.firstName),
            lastName: this.$options.filters.capitalize(this.lastName),
            email: this.email.toLowerCase(),
            phoneInternational: this.getPhoneInternationalE164(),
            subscribed: this.optInAgreement,
          }, tenantUrlLinksSuffix);

          this.turnInfo = response.data;
          this.turnNumber = this.turnInfo.waitingList.turnNumber;
          this.currentTurnNumber = this.turnInfo.currentTurnNumber;

          // Track success event in GTM
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'custom_form_submit',
            form_name: 'waitlist_form',
            page_path: '/waitlist/business-slug/customer-slug',
          });

          this.addWaitingListIdToPath(this.turnInfo.waitingList.id);
        } catch (err) {
          // Invalid tenant or submission error - redirect to 404 page
          await this.$router.push({ name: 'page-error-404' });
        } finally {
          this.$vs.loading.close();
        }
      }
    },
    handleFormStart() {
      if (!this.formStarted) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'custom_form_start',
          form_name: 'waitlist_form',
          page_path: '/waitlist/business-slug',
        });
        this.formStarted = true;
      }
    },
  },
};
</script>

<style lang="scss">

.waiting-list-form {
  // Fix for mobile scrolling issues
  &.waiting-list-form--full {
    // Use viewport helper variable with fallback
    min-height: 100vh;
    min-height: calc(var(--vh, 1vh) * 100);
    position: relative;
    
    // iOS specific height handling
    @supports (-webkit-touch-callout: none) {
      min-height: -webkit-fill-available;
    }
  }
  
  // Use flex-grow instead of flex-1 to avoid height calculation issues
  .flex-grow {
    flex-grow: 1;
  }
  // Card responsive pattern from success page
  .hosted-card {
    animation: slideUp 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275);

    // Mobile: Hide card styling
    @media (max-width: 575.98px) {
      &.vx-card {
        background: transparent;
        border: none;
        box-shadow: none;
      }
    }

    // Desktop: Show card with top accent
    @media (min-width: 576px) {
      position: relative;
      overflow: hidden;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: v-bind('accentColourLocal');
        z-index: 1;
      }
    }
  }

  @keyframes slideUp {
    0% {
      transform: translateY(30px);
      opacity: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 1;
    }
  }

  // Checkbox styling with dynamic accent color
  .vs-checkbox--check {
    background: v-bind('accentColourLocal') !important;
  }

  // Force tick color to use accent contrast color
  .vs-checkbox--icon {
    color: v-bind('accentContrastTextColor') !important;
  }

  // Checkbox border on checked state
  .vs-checkbox-primary input:checked + .vs-checkbox {
    border: 2px solid v-bind('accentColourLocal') !important;
    background: v-bind('accentColourLocal') !important;
  }

  // Checkbox hover state
  .vs-checkbox:hover {
    border-color: v-bind('accentColourLocal') !important;
  }

  .checkbox_x.vs-checkbox {
    @apply w-5 h-5;
  }

  .con-vs-avatar {
    @apply bg-white;
  }

  // Button hover and active states
  .send-button {
    transition: all 0.2s ease !important;

    &:hover {
      border: 1px solid v-bind('accentColourLocal') !important;
      box-shadow: 0 8px 25px -8px v-bind('accentColourLocal') !important;
      transform: translateY(-2px);
    }

    &:active {
      transform: translateY(0);
      box-shadow: 0 4px 15px -6px v-bind('accentColourLocal') !important;
    }
  }

  .vti__dropdown-list {
    z-index: 9999 !important;
  }

  // Style vue-tel-input to match vs-input fields with accent colors
  .vue-tel-input {
    border: 1px solid rgba(0, 0, 0, 0.2) !important;
    border-radius: 5px !important;
    transition: all 0.15s ease !important;

    &:hover {
      border-color: v-bind('accentColourLocal') !important;
    }

    &:focus-within {
      border-color: v-bind('accentColourLocal') !important;
    }

    &:active {
      border-color: v-bind('accentColourLocal') !important;
    }

    .vti__input {
      border: none !important;
      outline: none !important;
      padding: 10px 12px !important;
      font-size: 0.875rem !important;
    }

    .vti__dropdown {
      border-right: 1px solid rgba(0, 0, 0, 0.1) !important;
      background: transparent !important;

      &:hover {
        background: rgba(0, 0, 0, 0.02) !important;
      }
    }
  }

  // VS Input field styling with accent colors
  .vs-input {
    &:hover .vs-inputx {
      border-color: v-bind('accentColourLocal') !important;
    }

    &.input-primary-focus .vs-inputx {
      border-color: v-bind('accentColourLocal') !important;
    }

    &.input-primary-active .vs-inputx {
      border-color: v-bind('accentColourLocal') !important;
    }
  }

  // Override default vs-input focus styles
  .vs-input-primary .vs-input--input:focus {
    border: 1px solid v-bind('accentColourLocal') !important;
  }

  // Prevent label color changes on focus
  .vs-input-primary.isFocus .vs-input--label,
  .vs-input-primary.vs-input--input:focus .vs-input--label {
    color: inherit !important;
  }

  // Privacy policy link styling
  .privacy-policy-link {
    color: rgba(var(--vs-grey-dark), 1) !important;
    text-decoration: underline;

    &:hover {
      opacity: 0.8;
    }
  }
}
</style>
